import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { Alert, Checkbox } from "@mui/material";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  Menu,
  TextField,
  Typography,
  FormControlLabel,
  FormLabel,
  Stack,
  Tooltip,
} from "@mui/material";
import Contacts from "./contacts";
import FilterPanel from "./filterPanel";
import CustomFieldInput from "./customFieldInput";
import { Box } from "@mui/system";
import ConfirmDelete from "./confirmDelete";
import {
  AddCircleOutline,
  DeleteOutline,
  EditOutlined,
  Minimize,
  SettingsOutlined,
} from "@mui/icons-material";

import {
  changeCID,
  setCIDS,
  setData,
  setContact,
  setContacts,
  setParcelID,
  setOwnerName,
  setOwnerMailing,
  setBuildingAddr,
  setAssessorCIDValue,
  setExempt,
  setPropertyDesc,
  setSearchStatus,
  resetForm,
  setMils,
  setCID,
  setMarker,
  setCustomFieldValue,
  clearFilters,
} from "../features/assessorSlice";
import { searchParcel } from "../services/lookupService";
import { addProperty } from "../services/lookupService";
import { useEffect, useMemo, useState } from "react";
import CreateCID from "./createCID";
import {
  getCIDProperties,
  getCIDS,
  getCIDContacts,
  getCIDInfo,
} from "../services/cidService";
import { useDispatch, useSelector } from "react-redux";
import { updateProperty } from "../services/propertyService";
import { resetContacts } from "../features/contactSlice";
import { deleteCID } from "../services/cidService";
import ModifyCID from "./modifyCID";
import {
  changeName,
  changeNotes,
  loadLegend,
} from "../features/cidSlice";

import { markerOptionsWithLabels, NO_MARKER_COLOR } from "../markerColors";

const EMPTY_MARKER = {
  value: "",
  color: NO_MARKER_COLOR,
  label: "No marker",
};

// Joins contact rows onto property rows for the table. The contact's
// custom_fields is renamed contact_custom_fields so it can never clobber the
// property's own custom_fields in the spread.
const joinContactsToProperties = (properties, resContacts) => {
  const contacts = [];
  if (resContacts) {
    resContacts.forEach((c) => {
      if (c !== null) {
        const { id, custom_fields, ...rest } = c;
        contacts.push({ ...rest, contact_custom_fields: custom_fields || {} });
      }
    });
  }

  return (properties || []).map((p) => {
    if (!p.contact_id) {
      return { ...p };
    }
    const contactValue = contacts.find(
      (c) => c.contactTransferID === p.contact_id
    );
    return { ...p, ...contactValue };
  });
};

const LookupTool = (props) => {
  const [dialogOpen, toggleDialog] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [confirmDialog, toggleConfirmDialog] = useState(false);
  const [modifyDialog, toggleModifyDialog] = useState(false);
  const [cidActionsAnchorEl, setCidActionsAnchorEl] = useState(null);
  const cidActionsOpen = Boolean(cidActionsAnchorEl);
  const searchStatus = useSelector(
    (state) => state.assessor.value.searchStatus
  );
  const dispatch = useDispatch();
  const form = useSelector((state) => state.assessor.value.form);
  const assessor = useSelector((state) => state.assessor.value);
  const admin = useSelector((state) => state.auth.value.admin);
  const legend = useSelector((state) => state.cid.value.legend);
  const propertyFieldDefs = useSelector((state) => state.settings.value.propertyFields);
  const propertyFields = useMemo(
    () => propertyFieldDefs.filter((f) => !f.archived),
    [propertyFieldDefs]
  );

  const markerOptions = markerOptionsWithLabels(legend);

  const openCidActions = (event) => {
    setCidActionsAnchorEl(event.currentTarget);
  };

  const closeCidActions = () => {
    setCidActionsAnchorEl(null);
  };

  const handleChangeCID = async (e) => {
    dispatch(setCID(e.target.value));
    dispatch(setContacts([]));
    dispatch(clearFilters());
    setDataLoading(true);
    const cidData = await getCIDInfo(e.target.value);
    dispatch(changeNotes(cidData.notes));
    dispatch(changeName(cidData.name));
    dispatch(loadLegend(cidData.legend));
    const res = await getCIDProperties(e.target.value);
    const resContacts = await getCIDContacts(e.target.value);

    dispatch(setData(joinContactsToProperties(res?.data, resContacts)));
    setDataLoading(false);

    assessor.cids.forEach((c) => {
      if (c.id === e.target.value) {
        dispatch(setMils(c.mils));
      }
    });
  };

  const onPush = async () => {
    if (form.transferID === -1) {
      addProperty(form).then(async () => {
        const refreshProperties = await getCIDProperties(form.cid);
        const resContacts = await getCIDContacts(form.cid);
        dispatch(setData(joinContactsToProperties(refreshProperties?.data, resContacts)));
        setDataLoading(false);
      });
    } else {
      updateProperty(form).then(async () => {
        const refreshProperties = await getCIDProperties(form.cid);
        const resContacts = await getCIDContacts(form.cid);
        dispatch(setData(joinContactsToProperties(refreshProperties?.data, resContacts)));
        setDataLoading(false);
      });
    }
  };

  const handleDelete = async () => {
    toggleConfirmDialog(false);
    return new Promise((res) => {
      dispatch(resetForm());
      dispatch(changeCID({ cid: "", data: [] }));
      res(deleteCID(form.cid));
    })
      .then(async () => {
        const cids = await getCIDS();
        dispatch(setCIDS(cids));
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onSubmit = async (manualEntry) => {
    const { cid, parcelID } = form;
    if (cid === "") {
      dispatch(setSearchStatus(1));
    } else {
      if (manualEntry) {
        dispatch(setSearchStatus(2));
        dispatch(setOwnerName(""));
        dispatch(setOwnerMailing(""));
        dispatch(setBuildingAddr(""));
        dispatch(setPropertyDesc(""));
        dispatch(setContact(null));
        dispatch(setContacts([]));
        dispatch(resetContacts());
        dispatch(setAssessorCIDValue(0));
      } else {
        dispatch(setSearchStatus(1));
        const res = await searchParcel({
          cid: cid,
          parcelID: parcelID.trim().replaceAll("-", " "),
        });

        if (res) {
          dispatch(setSearchStatus(2));
          dispatch(setParcelID(res.parcelID));
          dispatch(setOwnerName(res.ownerName));
          dispatch(setOwnerMailing(res.ownerMailing));
          dispatch(setBuildingAddr(res.buildingAddress));

          if (form.transferID === -1) {
            dispatch(resetContacts());
            dispatch(setContact(null));
            dispatch(setContacts([]));
            dispatch(setPropertyDesc(""));
          }

          dispatch(setAssessorCIDValue(res.assessorInfo));
        } else {
          dispatch(setSearchStatus(2));
          dispatch(setOwnerName("NOT FOUND"));
          dispatch(setOwnerMailing("NOT FOUND"));
          dispatch(setBuildingAddr("NOT FOUND"));
          dispatch(setPropertyDesc(""));
          dispatch(setContact(null));
          dispatch(setContacts([]));
          dispatch(resetContacts());
          dispatch(setAssessorCIDValue(0));
        }
      }
    }
  };

  useEffect(() => {
    async function reqCIDS() {
      const res = await getCIDS();
      if (res) {
        dispatch(setCIDS(res));
      }
    }
    reqCIDS();
  }, [dispatch]);

  return (
    <Grid
      container
      spacing={3}
      textAlign="center"
      p={1}
      justifyContent="center"
    >
      <CreateCID dialogOpen={dialogOpen} handleClose={toggleDialog} />
      <ConfirmDelete
        dialogOpen={confirmDialog}
        handleClose={toggleConfirmDialog}
        deleteCID={handleDelete}
      />
      <ModifyCID dialogOpen={modifyDialog} handleClose={toggleModifyDialog} />
      <Grid item xs={12} mx="auto">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Minimize />}
            onClick={() => props.toggleLookup()}
          >
            Hide Editor
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {form.cid ? (
              <Typography variant="body2" color="text.secondary">
                CID #{form.cid}
              </Typography>
            ) : null}
            <Tooltip title="CID actions">
              <IconButton color="primary" onClick={openCidActions}>
                <SettingsOutlined />
              </IconButton>
            </Tooltip>
          </Box>
          <Menu
            anchorEl={cidActionsAnchorEl}
            open={cidActionsOpen}
            onClose={closeCidActions}
          >
            <MenuItem
              onClick={() => {
                closeCidActions();
                toggleDialog(true);
              }}
            >
              <AddCircleOutline sx={{ mr: 1 }} />
              Create CID
            </MenuItem>
            {admin ? (
              <MenuItem
                disabled={!form.cid}
                onClick={() => {
                  closeCidActions();
                  toggleModifyDialog(true);
                }}
              >
                <EditOutlined sx={{ mr: 1 }} />
                Modify CID
              </MenuItem>
            ) : null}
            {admin ? (
              <MenuItem
                disabled={!form.cid}
                onClick={() => {
                  closeCidActions();
                  toggleConfirmDialog(true);
                }}
              >
                <DeleteOutline sx={{ mr: 1 }} />
                Delete CID
              </MenuItem>
            ) : null}
          </Menu>
        </Box>
      </Grid>

      {!assessor.form.cid && searchStatus === 2 ? (
        <Alert sx={{ mt: 1 }} severity="error">
          Must select a CID
        </Alert>
      ) : (
        <></>
      )}

      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>CID</InputLabel>
          <Select
            value={form.cid}
            onChange={(e) => handleChangeCID(e)}
            label="CID"
            fullWidth
          >
            {assessor.cids !== null
              ? assessor.cids.map((element, index) => {
                  return (
                    <MenuItem key={index} value={element.id}>
                      {element.name}
                    </MenuItem>
                  );
                })
              : null}
          </Select>
        </FormControl>
      </Grid>
      {form.cid ? (
        <Grid item xs={12}>
          <FilterPanel />
        </Grid>
      ) : null}
      {dataLoading ? (
        <Grid item xs={12}>
          <CircularProgress />
        </Grid>
      ) : (
        <></>
      )}

      <Grid item xs={12}>
        <TextField
          value={form.parcelID}
          fullWidth
          label="Parcel ID"
          onChange={(e) => dispatch(setParcelID(e.target.value))}
        />
      </Grid>
      <Grid item xs={3}>
        <Button
          variant="outlined"
          onClick={() => {
            dispatch(resetForm());
          }}
        >
          Clear
        </Button>
      </Grid>
      <Grid item xs={5}>
        <Button
          variant="outlined"
          onClick={() => onSubmit(true)}
          type="submit"
        >
          Manual Entry
        </Button>
      </Grid>
      <Grid item xs={3}>
        <Button variant="contained" onClick={() => onSubmit()} type="submit">
          Search
        </Button>
      </Grid>

      {searchStatus === 1 ? (
        <Grid item xs={12}>
          <CircularProgress />
        </Grid>
      ) : (
        <></>
      )}
      {searchStatus === 2 ? (
        <>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel id="markerColor">Marker Color</FormLabel>

              <Select
                fullWidth
                labelId="markerColor"
                size="small"
                value={form.flagColor || ""}
                onChange={(e) => dispatch(setMarker(e.target.value || null))}
                renderValue={(selected) => {
                  const selectedOption =
                    markerOptions.find((option) => option.value === selected) || EMPTY_MARKER;
                  return (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: selectedOption.color,
                        }}
                      />
                      <Typography variant="body2">{selectedOption.label}</Typography>
                    </Stack>
                  );
                }}
              >
                {markerOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          backgroundColor: option.color,
                        }}
                      />
                      <Typography variant="body2">{option.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
                <MenuItem value="">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: EMPTY_MARKER.color,
                      }}
                    />
                    <Typography variant="body2">{EMPTY_MARKER.label}</Typography>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              onChange={(e) => dispatch(setOwnerName(e.target.value))}
              fullWidth
              label="Owner Name"
              value={form.ownerName}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              onChange={(e) => dispatch(setOwnerMailing(e.target.value))}
              fullWidth
              label="Owner Mailing Address"
              value={form.ownerMailing}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              onChange={(e) => dispatch(setBuildingAddr(e.target.value))}
              fullWidth
              label="Building Address"
              value={form.buildingAddress}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              label="Exempt from tax"
              control={
                <Checkbox
                  color="error"
                  checked={form.exempt}
                  onChange={() => dispatch(setExempt(!form.exempt))}
                />
              }
            />
          </Grid>
          {form.exempt ? (
            <></>
          ) : (
            <>
              <Grid item xs={12}>
                <FormControl>
                  <InputLabel>Assessor Value</InputLabel>
                  <Input
                    startAdornment={
                      <InputAdornment position="start">$</InputAdornment>
                    }
                    type="tel"
                    fullWidth
                    onChange={(e) => {
                      if (!e.target.value) {
                        dispatch(setAssessorCIDValue(0));
                      } else {
                        dispatch(
                          setAssessorCIDValue(parseFloat(e.target.value))
                        );
                      }
                    }}
                    value={form.assessorValue}
                    label="Assessor Value"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">
                  CID Value: $ {form.cidValue.toFixed(2)}
                </Typography>
              </Grid>
            </>
          )}

          <Grid item xs={12} mx="auto">
            <TextField
              minRows={7}
              multiline
              fullWidth
              onChange={(e) => dispatch(setPropertyDesc(e.target.value))}
              label="Property Description"
              value={form.propertyDesc}
            />
          </Grid>

          {propertyFields.map((def) => (
            <Grid item xs={12} key={def.id}>
              <CustomFieldInput
                def={def}
                value={form.customFields?.[def.internal_name]}
                onChange={(value) =>
                  dispatch(setCustomFieldValue({ name: def.internal_name, value }))
                }
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Contacts mode="embedded" />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={() => onPush()}
              disabled={!form.parcelID}
            >
              {form.transferID === -1
                ? "Add Property Data"
                : "Update Property Data"}
            </Button>
          </Grid>
        </>
      ) : (
        <></>
      )}
    </Grid>
  );
};

export default LookupTool;
